import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicDoctors } from "@/hooks/useCMSDoctors";
import { getLocalized } from "@/utils/localization";
import i18n from "@/i18n";

const getYYYYMMDD = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const MORNING_SLOTS = ["08:00 ص","08:30 ص","09:00 ص","09:30 ص","10:00 ص","10:30 ص","11:00 ص","11:30 ص"];
const AFTERNOON_SLOTS = ["12:00 م","12:30 م","01:00 م","01:30 م","02:00 م","02:30 م"];
const EVENING_SLOTS = ["04:00 م","04:30 م","05:00 م","05:30 م","06:00 م","06:30 م","07:00 م","07:30 م"];

// Simulate some unavailable slots
const UNAVAILABLE = new Set(["09:00 ص","10:30 ص","05:00 م","01:00 م"]);

interface StepDateTimeProps {
  doctorId: string;
  selectedDate: string;
  selectedTime: string;
  onSelectDate: (date: string) => void;
  onSelectTime: (time: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function StepDateTime({
  doctorId,
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
  onNext,
  onBack,
}: StepDateTimeProps) {
  const { t } = useTranslation();
  const [weekOffset, setWeekOffset] = useState(0);
  const { doctors: doctorsDetailed } = usePublicDoctors();

  const DAY_NAMES = [
    t("days.sun"),
    t("days.mon"),
    t("days.tue"),
    t("days.wed"),
    t("days.thu"),
    t("days.fri"),
    t("days.sat")
  ];
  const MONTH_NAMES = [
    t("months.jan"),
    t("months.feb"),
    t("months.mar"),
    t("months.apr"),
    t("months.may"),
    t("months.jun"),
    t("months.jul"),
    t("months.aug"),
    t("months.sep"),
    t("months.oct"),
    t("months.nov"),
    t("months.dec")
  ];

  const formatSlot = (slot: string) => {
    if (i18n.language?.startsWith("en")) {
      return slot.replace("ص", "AM").replace("م", "PM");
    }
    return slot;
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return `${day} ${MONTH_NAMES[monthIndex]} ${year}`;
    }
    return dateStr;
  };

  const doctorIds = doctorId ? doctorId.split(",") : [];
  const selectedDoctors = doctorsDetailed.filter((d) => doctorIds.includes(d.id) || doctorIds.includes(d.doctor_id));

  // Generate 14 days starting from tomorrow, skip Fridays
  const allDates = Array.from({ length: 21 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1);
    return d;
  }).filter((d) => d.getDay() !== 5);

  // Show 7 days per week
  const weekDates = allDates.slice(weekOffset * 7, weekOffset * 7 + 7);
  const maxWeek = Math.floor((allDates.length - 1) / 7);

  const handleDateSelect = (dateStr: string) => {
    onSelectDate(dateStr);
    onSelectTime("");
  };

  const SlotGroup = ({ label, icon, slots }: { label: string; icon: string; slots: string[] }) => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-brand-forest-500 text-sm`}></i>
        </div>
        <span className="text-xs font-semibold text-gray-500">{label}</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {slots.map((slot) => {
          const isUnavailable = UNAVAILABLE.has(slot);
          const isSelected = selectedTime === slot;
          return (
            <button
              key={slot}
              onClick={() => !isUnavailable && onSelectTime(slot)}
              disabled={isUnavailable}
              className={`py-2 px-1 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap ${
                isUnavailable
                  ? "bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                  : isSelected
                  ? "bg-brand-forest-600 text-white cursor-pointer scale-105"
                  : "bg-brand-cream-50 text-gray-700 hover:bg-brand-cream-100 hover:text-brand-forest-700 cursor-pointer border border-brand-cream-200"
              }`}
            >
              {formatSlot(slot)}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      {/* Doctor context */}
      {selectedDoctors.length > 0 && (
        <div className="bg-brand-cream-50 border border-brand-cream-200 rounded-xl p-3 mb-5 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400 font-semibold">{t("booking.step3.selected_doctors")}</span>
            <button onClick={onBack} className="text-xs text-brand-forest-500 font-semibold hover:underline cursor-pointer whitespace-nowrap">
              {t("booking.step2.change")}
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {selectedDoctors.map((doc) => {
              const docName = getLocalized(i18n.language, doc.name, doc.name_en);
              const docSpecialty = getLocalized(i18n.language, doc.specialty, doc.specialty_en);
              return (
                <div key={doc.id} className="flex items-center gap-3">
                  <img src={doc.image} alt={docName} className="w-8 h-8 rounded-lg object-cover object-top flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">{docName}</p>
                    <p className="text-[10px] text-brand-forest-600 truncate">{docSpecialty}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-5">
        <h2 className="text-xl font-black text-gray-900 mb-1">{t("booking.step3.heading")}</h2>
        <p className="text-gray-400 text-sm">{t("booking.step3.desc")}</p>
      </div>

      {/* Date picker */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">{t("booking.confirm.date")}</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
              disabled={weekOffset === 0}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                weekOffset === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-brand-cream-100"
              }`}
            >
              <i className={i18n.language === "ar" ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"}></i>
            </button>
            <span className="text-xs text-gray-400 px-1">
              {weekOffset === 0 ? t("booking.step3.this_week") : weekOffset === 1 ? t("booking.step3.next_week") : t("booking.step3.week_num", { num: weekOffset + 1 })}
            </span>
            <button
              onClick={() => setWeekOffset((w) => Math.min(maxWeek, w + 1))}
              disabled={weekOffset >= maxWeek}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                weekOffset >= maxWeek ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-brand-cream-100"
              }`}
            >
              <i className={i18n.language === "ar" ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}></i>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {weekDates.map((d) => {
            const dateVal = getYYYYMMDD(d);
            const dayStr  = DAY_NAMES[d.getDay()].slice(0, 3);
            const isSelected = selectedDate === dateVal;

            return (
              <button
                key={dateVal}
                onClick={() => handleDateSelect(dateVal)}
                className={`flex flex-col items-center py-2.5 px-1 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? "border-brand-forest-500 bg-brand-forest-600 text-white"
                    : "border-gray-100 bg-white hover:border-brand-cream-300 hover:bg-brand-cream-50"
                }`}
              >
                <span className={`text-[10px] font-medium ${isSelected ? "text-brand-cream-200" : "text-gray-400"}`}>{dayStr}</span>
                <span className={`text-base font-black mt-0.5 ${isSelected ? "text-white" : "text-gray-800"}`}>{d.getDate()}</span>
                <span className={`text-[9px] ${isSelected ? "text-brand-cream-200" : "text-gray-400"}`}>{MONTH_NAMES[d.getMonth()].slice(0, 3)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      {selectedDate ? (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">{t("booking.step3.available_time")}</p>
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 bg-brand-forest-600 rounded-sm"></span>
                {t("booking.step3.selected")}
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 bg-gray-100 rounded-sm border border-gray-200"></span>
                {t("booking.step3.booked")}
              </span>
            </div>
          </div>
          <SlotGroup label={t("booking.step3.morning")} icon="ri-sun-line" slots={MORNING_SLOTS} />
          <SlotGroup label={t("booking.step3.afternoon")} icon="ri-sun-foggy-line" slots={AFTERNOON_SLOTS} />
          <SlotGroup label={t("booking.step3.evening")} icon="ri-moon-line" slots={EVENING_SLOTS} />
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-brand-cream-50 border border-brand-cream-200 rounded-xl p-4 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-brand-cream-200 rounded-lg flex-shrink-0">
            <i className="ri-calendar-line text-brand-forest-600 text-sm"></i>
          </div>
          <p className="text-sm text-gray-500">{t("booking.step3.select_date_prompt")}</p>
        </div>
      )}

      {/* Selected summary */}
      {selectedDate && selectedTime && (
        <div className="flex items-center gap-3 bg-brand-forest-50 border border-brand-forest-200 rounded-xl p-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-brand-forest-100 rounded-lg flex-shrink-0">
            <i className="ri-calendar-check-line text-brand-forest-600 text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-bold text-brand-forest-800">{formatDateDisplay(selectedDate)} — {formatSlot(selectedTime)}</p>
            <p className="text-[10px] text-brand-forest-600">{t("booking.step3.slot_selected_badge")}</p>
          </div>
          <i className="ri-check-double-line text-brand-forest-600 text-base mr-auto"></i>
        </div>
      )}

      <div className="flex gap-3 mt-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 border-2 border-gray-200 text-gray-600 font-semibold px-5 py-3.5 rounded-full hover:border-gray-300 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className={i18n.language === "ar" ? "ri-arrow-right-line" : "ri-arrow-left-line"}></i>
          {t("btn.back")}
        </button>
        <button
          onClick={() => selectedDate && selectedTime && onNext()}
          disabled={!selectedDate || !selectedTime}
          className={`inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
            selectedDate && selectedTime
              ? "bg-brand-forest-600 hover:bg-brand-forest-700 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {t("booking.step3.continue")}
          <i className={i18n.language === "ar" ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
        </button>
      </div>
    </div>
  );
}
